import React, { useState } from "react";
import { LoadingState } from "../utils/enum";
import { DashboardContextModel } from "../models/Dashboard";
import { fetchLoadDetails, fetchLoads } from "../services/loadAPI";
import { Equipment, LoadData } from "../models";
import { fetchEquipmentWithLoads } from "../services/equipmentAPI";
import { loadCategoriesMap } from "../utils/constants";

type DashboardProviderProps = {
  children?: React.ReactNode;
};

// license plates of the loads' assigned equipment (the `lps` the
// loadAssigned equipment request takes)
export const getLoadAssignedLps = (loads: LoadData[]): string[] => {
  const lps: string[] = [];
  loads.forEach((load) => {
    if (load.equipmentId && load.carrierID && load.lp) {
      lps.push(load.lp);
    }
  });
  return lps;
};

export const DashboardContext = React.createContext<DashboardContextModel>(
  {} as DashboardContextModel
);

export const DashboardProvider = ({ children }: DashboardProviderProps) => {
  const [loads, setLoads] = useState<LoadData[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchedLoads, setSearchedLoads] = useState<LoadData[]>([]);
  const [searchedEquipment, setSearchedEquipment] = useState<Equipment[]>([]);
  const [subscribedEquipment, setSubscribedEquipment] = useState<Equipment[]>(
    []
  );
  const [loadingDetailsState, setLoadingDetailsState] = useState(
    LoadingState.None
  );
  const [loadingState, setLoadingState] = useState(LoadingState.None);
  const [loadedEquipment, setLoadedEquipment] = useState<Equipment[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedLoad, setSelectedLoad] = useState<LoadData>();

  const listMapData = async () => {
    setLoadingState(LoadingState.Pending);
    try {
      const loads = await fetchLoads({
        status: "pending,at_pickup,in_transit,at_dropoff,check_in_pickup",
      });
      const equipmentParams = getLoadAssignedLps(loads);
      const result = await fetchEquipmentWithLoads(equipmentParams);
      setLoads(loads);
      setLoadedEquipment(result.data);
      setLoadingState(LoadingState.Success);
    } catch (err) {
      console.error("Failed to list loads and equipment", err);
      setLoadingState(LoadingState.Error);
    }
  };

  const reloadMapDataSilently = async () => {
    try {
      const loads = await fetchLoads({
        status: "pending,at_pickup,in_transit,at_dropoff,check_in_pickup",
      });
      const equipmentParams = getLoadAssignedLps(loads);
      const result = await fetchEquipmentWithLoads(equipmentParams);
      setLoads(loads);
      setLoadedEquipment(result.data);
    } catch (err) {
      console.error("Failed to list loads", err);
      setLoadingState(LoadingState.Error);
    }
  };

  const removeLoad = (loadID: string) => {
    const updatedLoads = loads.filter((load) => load.loadNumID !== loadID);
    setLoads(updatedLoads);
  };

  const getLoadDetails = async (load: LoadData) => {
    try {
      setLoadingDetailsState(LoadingState.Pending);
      const result = await fetchLoadDetails(+load.loadID, !load.generateRoute);
      const updatedLoads = [...loads];
      const loadIndex = loads.findIndex(
        (item) => load.loadNumID === item.loadNumID
      );
      if (loadIndex >= 0) {
        updatedLoads[loadIndex] = result;
        setLoads(updatedLoads);
        setSelectedLoad(result);
      }
      setLoadingDetailsState(LoadingState.Success);
      return result;
    } catch (err) {
      console.error("Failed to list loads and equipment", err);
      setLoadingDetailsState(LoadingState.Error);
    }
  };

  const searchMapItems = (term: string) => {
    setSearchTerm(term);
    const lower = term.toLowerCase();
    searchLoads(lower);
    searchEquipment(lower);
  };

  const searchLoads = (term: string) => {
    const filtered = loads.filter(
      (l) =>
        l.pickupLocation.name.toLowerCase().includes(term) ||
        l.dropOffLocation.name.toLowerCase().includes(term) ||
        l.loadNumID.toLowerCase().includes(term) ||
        l.equipmentNum?.toLowerCase().includes(term) ||
        l.equipmentType?.toLowerCase().includes(term) ||
        l.lp?.toLowerCase().includes(term) ||
        l.driver?.name.toLowerCase().includes(term) ||
        loadCategoriesMap[l.category ?? ""].toLowerCase().includes(term) ||
        l.driver?.email.toLowerCase().includes(term)
    );
    setSearchedLoads(filtered);
  };

  const searchEquipment = (term: string) => {
    const filtered = loadedEquipment.filter(
      (l) =>
        l.lp.toLowerCase().includes(term) ||
        l.num.toLowerCase().includes(term) ||
        l.equipmentID.toLowerCase().includes(term) ||
        l.type?.toLowerCase().includes(term) ||
        l.trailerSubType?.toLowerCase().includes(term) ||
        l.lp?.toLowerCase().includes(term) ||
        l.driver?.name.toLowerCase().includes(term) ||
        l.driver?.email.toLowerCase().includes(term)
    );
    setSearchedEquipment(filtered);
  };

  const values: DashboardContextModel = {
    searchTerm,
    selectedLoad,
    subscribedEquipment,
    loads,
    searchedLoads,
    loadingState,
    loadedEquipment,
    searchedEquipment,
    loadingDetailsState,
    searchOpen,
    setSearchTerm,
    setSelectedLoad,
    reloadMapDataSilently,
    removeLoad,
    setLoadingState,
    setSubscribedEquipment,
    getLoadDetails,
    setSearchOpen,
    searchMapItems,
    listMapData,
  };

  return (
    <DashboardContext.Provider value={values}>
      {children}
    </DashboardContext.Provider>
  );
};
